// src/components/Dashboard/SLARequirementCompliance.tsx
import React, { useState } from "react";
import { Spin, Alert, Collapse } from "antd";
import {
  LoadingOutlined,
  FileTextOutlined,
  IdcardOutlined,
  HomeOutlined,
  DollarOutlined,
  FormOutlined,
  ClockCircleOutlined,
  BarsOutlined,
} from "@ant-design/icons";
import api from "../../api";

const { Panel } = Collapse;

interface SLARequirementComplianceProps {
  dateRange: [string, string] | null;
  selectedUser?: string;
  period?: string;
  filterType?: "preset" | "custom";
}

const SLARequirementCompliance: React.FC<SLARequirementComplianceProps> = ({
  dateRange,
  selectedUser,
  period,
  filterType,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const hasValidFilters = (): boolean => {
    if (filterType === "preset" && period) {
      return true;
    }
    if (filterType === "custom" && dateRange && dateRange[0] && dateRange[1]) {
      return true;
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
      return true;
    }
    return false;
  };

  const fetchRequirementData = async () => {
    if (loading || !hasValidFilters()) {
      console.log(
        "SLARequirementCompliance - Skipping fetch: loading or invalid filters",
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let params: Record<string, string> = {};

      if (filterType === "preset" && period) {
        const periodMapping: Record<string, string> = {
          week: "last_7_days",
          month: "last_30_days",
          quarter: "last_90_days",
        };

        params.period = periodMapping[period] || period;
        console.log(
          `SLARequirementCompliance - Period mapping: ${period} → ${params.period}`,
        );
      } else if (filterType === "custom" && dateRange) {
        params.start_date = dateRange[0];
        params.end_date = dateRange[1];
      } else if (dateRange) {
        // Fallback for when dateRange is provided without filterType
        params.start_date = dateRange[0];
        params.end_date = dateRange[1];
      } else {
        setLoading(false);
        return;
      }

      if (selectedUser) {
        params.user_id = selectedUser;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await api.get(
        `/metrics/sla/requirements/?${queryString}`,
      );

      setData(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching requirement compliance data:", err);
      setError(
        `Failed to load requirement compliance data: ${err.response?.data?.message || err.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: string | string[]) => {
    const isOpening = Array.isArray(key) ? key.length > 0 : key;
    if (isOpening && !loading && hasValidFilters()) {
      fetchRequirementData();
    }
  };

  const getRequirementTypeIcon = (type: string) => {
    switch (type) {
      case "photo_id":
        return <IdcardOutlined className="text-blue-600" />;
      case "proof_of_address":
        return <HomeOutlined className="text-green-600" />;
      case "proof_of_income":
        return <DollarOutlined className="text-yellow-600" />;
      case "form":
        return <FormOutlined className="text-purple-600" />;
      default:
        return <FileTextOutlined className="text-gray-600" />;
    }
  };

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 95) return "#10b981"; // Green
    if (percentage >= 85) return "#f59e0b"; // Yellow
    if (percentage >= 70) return "#f97316"; // Orange
    return "#ef4444"; // Red
  };

  const getComplianceColorClass = (percentage: number) => {
    if (percentage >= 95) return "text-green-600";
    if (percentage >= 85) return "text-yellow-600";
    if (percentage >= 70) return "text-orange-600";
    return "text-red-600";
  };

  const renderRequirementCards = () => {
    if (!data || !data.requirement_compliance) return null;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {data.requirement_compliance.map((req: any, index: number) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
          >
            {/* Header */}
            <div className="flex items-start mb-3">
              <div className="mr-3 mt-1">
                {getRequirementTypeIcon(req.requirement_type)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 leading-tight mb-1">
                  {req.requirement_name}
                </h4>
                <p className="text-xs text-gray-500">
                  {req.requirement_slug.replace("_", " ")}
                </p>
              </div>
            </div>

            {/* Overall Compliance */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">
                  Cumplimiento General
                </span>
                <span
                  className={`text-sm font-bold ${getComplianceColorClass(req.combined_compliance_pct)}`}
                >
                  {req.combined_compliance_pct.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(req.combined_compliance_pct, 100)}%`,
                    backgroundColor: getComplianceColor(
                      req.combined_compliance_pct,
                    ),
                  }}
                />
              </div>
            </div>

            {/* Before/After Cutoff */}
            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
              <div className="text-center">
                <div className={`font-semibold`}>
                  {req.before_cutoff_compliance_pct.toFixed(1)}%
                </div>
                <div className="text-gray-500">Primer bloque</div>
              </div>
              <div className="text-center">
                <div className={`font-semibold`}>
                  {req.after_cutoff_compliance_pct.toFixed(1)}%
                </div>
                <div className="text-gray-500">Segundo bloque</div>
              </div>
            </div>

            {/* Stats */}
            <div className="border-t border-gray-100 pt-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="font-semibold text-gray-900">
                    {req.total_processed}
                  </div>
                  <div className="text-gray-500">Procesados</div>
                </div>
                <div>
                  <div className="font-semibold text-green-600">
                    {req.compliant}
                  </div>
                  <div className="text-gray-500">Cumplidos</div>
                </div>
              </div>
              <div className="mt-2 text-center">
                <div className="flex items-center justify-center text-xs text-gray-500">
                  <ClockCircleOutlined className="mr-1" />
                  {req.avg_verification_time_business_hours?.toFixed(1)}h
                  promedio
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Collapse onChange={handleToggle} className="mb-6">
      <Panel
        header={
          <div className="flex items-center">
            <BarsOutlined className="mr-2 text-blue-600" />
            <span className="font-medium">
              Cumplimiento SLA por Tipo de Requisito
            </span>
            {data && (
              <span className="ml-2 text-sm text-gray-500">
                ({data.total_requirement_types} tipos)
              </span>
            )}
          </div>
        }
        key="requirement-compliance"
      >
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              className="mr-3"
            />
            <span className="text-gray-600">
              Cargando datos de cumplimiento por requisito...
            </span>
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        {data && !loading && (
          <div>
            {/* Summary Stats */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {data.total_requirement_types}
                  </div>
                  <div className="text-sm text-gray-600">
                    Tipos de Requisitos
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {data.requirement_compliance
                      ?.reduce(
                        (sum: number, req: any) => sum + req.total_processed,
                        0,
                      )
                      .toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Procesados</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {data.requirement_compliance
                      ?.reduce(
                        (sum: number, req: any) => sum + req.compliant,
                        0,
                      )
                      .toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Cumplidos</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">
                    {(
                      (data.requirement_compliance?.reduce(
                        (sum: number, req: any) => sum + req.compliant,
                        0,
                      ) /
                        data.requirement_compliance?.reduce(
                          (sum: number, req: any) => sum + req.total_processed,
                          0,
                        )) *
                      100
                    ).toFixed(1)}
                    %
                  </div>
                  <div className="text-sm text-gray-600">
                    Cumplimiento Promedio
                  </div>
                </div>
              </div>
            </div>
            {/* Requirement Cards */}
            {renderRequirementCards()}
          </div>
        )}
      </Panel>
    </Collapse>
  );
};

export default SLARequirementCompliance;
